import type { BasketItem } from '../types/order';
import i18n from '../i18n';
import { hidesSizeName } from './sellableSizes';

/**
 * Η σημείωση γραμμής delivery («Size: X | Without: N ingredients | Extra: … | οδηγίες»)
 * που στέλνει το DeliveryWithMap ως `notes` και καταλήγει στο product_notes (ουρά,
 * προεπισκόπηση, επανεκτύπωση).
 *
 * Απόφαση 15 / F1-S1 (χωρίς διακόπτη): παραλείπεται ΠΑΝΤΑ το «Size: X» όταν το X είναι
 * το ΜΟΝΑΔΙΚΟ πωλήσιμο μέγεθος — ίδια συνθήκη με τον backend (orders.js, BE-16, που κόβει το
 * ίδιο τμήμα και από το δικό του κείμενο). Το μέγεθος ΔΕΝ χάνεται: το payload κρατά το
 * `selectedSize` αυτούσιο και ο backend γράφει internal_size_id.
 * Προϊόν-τέλος (απόφαση F1-W4-4): το «Size:» παραλείπεται ΠΑΝΤΑ.
 * Καμία επίδραση σε ποσό. Κατά τα άλλα η σημείωση είναι ίδια χαρακτήρα προς χαρακτήρα με
 * ό,τι έχτιζε το DeliveryWithMap ως τώρα.
 */
export function buildDeliveryItemNotes(
  item: Pick<BasketItem, 'product' | 'selectedSize' | 'removedIngredients' | 'addedExtras' | 'specialInstructions'>
): string {
  const noteParts: string[] = [];
  if (item.selectedSize && !hidesSizeName(item.product, item.selectedSize.id)) {
    noteParts.push(`Size: ${item.selectedSize.name}`);
  }
  if (item.removedIngredients && item.removedIngredients.length > 0) {
    noteParts.push(`Without: ${item.removedIngredients.length} ingredients`);
  }
  if (item.addedExtras && item.addedExtras.length > 0) {
    const extraStrs = item.addedExtras.map(e =>
      e.quantity > 1 ? `${e.name} x${e.quantity}` : e.name
    );
    noteParts.push(`Extra: ${extraStrs.join(', ')}`);
  }
  if (item.specialInstructions) noteParts.push(item.specialInstructions);
  return noteParts.join(' | ');
}

/**
 * Ονόματα που δεν ξεχωρίζουν μια παραγγελία: η προεπιλογή του Take Away («Πελάτης» — έτσι λέγονταν
 * ΚΑΙ οι 618 take away της εβδομάδας 5–12/10/2026 στην παραγωγή) και τα παλιά αγγλικά του backend.
 */
function isPlaceholderName(name: unknown): boolean {
  const n = String(name ?? '').trim().toLowerCase();
  if (!n) return true;
  return n === 'walk-in customer' || n === 'unknown customer'
    || n === String(i18n.t('takeaway:customerDefault', { lng: 'el' })).toLowerCase()
    || n === String(i18n.t('takeaway:customerDefault', { lng: 'en' })).toLowerCase();
}

/**
 * «2× Κουλούρι · Κρουασάν · +2» — τι έχει μέσα μια παραγγελία χωρίς πραγματικό όνομα πελάτη, ώστε
 * να ξεχωρίζει στη λίστα. Ίδιο προϊόν σε πολλές γραμμές αθροίζεται· τα ζυγιζόμενα χωρίς «×».
 */
export function itemsSummary(items: unknown, max = 3): string {
  if (!Array.isArray(items) || items.length === 0) return '';
  const lines: { name: string; quantity: number; weighed: boolean }[] = [];
  items.forEach((item: any) => {
    const name = String(item?.product_name || '').trim();
    if (!name) return;
    const weighed = item?.weight != null;
    const quantity = Number(item?.quantity) || 1;
    const same = !weighed && lines.find(l => l.name === name && !l.weighed);
    if (same) same.quantity += quantity;
    else lines.push({ name, quantity, weighed });
  });
  const shown = lines.slice(0, max).map(l => (l.quantity > 1 && !l.weighed ? `${l.quantity}× ${l.name}` : l.name));
  const rest = lines.length - shown.length;
  return rest > 0 ? `${shown.join(' · ')} · +${rest}` : shown.join(' · ');
}

// Helper to extract customer information from different platform order formats
export interface ExtractedCustomerInfo {
  name: string;
  phone: string;
  address: string;
  floor: string | null;
  doorbell: string | null;
  isTakeaway: boolean;
  isPreOrder: boolean;
  preOrderTime: Date | null;
  notes: string;
  /** Σύνοψη ειδών όταν το όνομα δεν ξεχωρίζει την παραγγελία («Πελάτης» του Take Away)· αλλιώς ''. */
  summary: string;
}

export function extractCustomerInfo(order: any): ExtractedCustomerInfo {
  const orderJson = order?.order_json || {};
  const unknownCustomer = i18n.t('orders:unknownCustomer');
  let name = unknownCustomer;
  let phone = '';
  let address = '';
  let floor: string | null = null;
  let doorbell: string | null = null;
  let isTakeaway = false;
  let isPreOrder = false;
  let preOrderTime: Date | null = null;
  let notes = '';
  let summary = '';

  if (order.platform_name === 'internal') {
    const customer = orderJson.customer || {};
    if (isPlaceholderName(customer.name)) {
      name = i18n.t('orders:walkInCustomer');
      summary = itemsSummary(orderJson.items);
    } else {
      name = customer.name;
    }
    if (orderJson.type === 'takeaway') {
      isTakeaway = true;
      notes = customer.notes || '';
      address = '';
    } else {
      phone = customer.phone || '';
      address = customer.address || '';
      floor = customer.floor || null;
      doorbell = customer.doorbell || null;
    }
  } else if (order.platform_name === 'wolt') {
    name = orderJson.consumer_name || unknownCustomer;
    phone = orderJson.consumer_phone_number || '';
    isTakeaway = orderJson.delivery?.type === 'takeaway';
    isPreOrder = !!(orderJson.type === 'preorder' && orderJson.pre_order?.preorder_time);
    if (isPreOrder) {
      preOrderTime = new Date(orderJson.pre_order.preorder_time);
    }
    if (!isTakeaway && orderJson.delivery?.location) {
      const loc = orderJson.delivery.location;
      const parts = [];
      if (loc.street_address) parts.push(loc.street_address);
      if (loc.apartment) parts.push(i18n.t('orders:apartment', { value: loc.apartment }));
      if (loc.zip_code) parts.push(loc.zip_code);
      if (loc.city) parts.push(loc.city);
      address = parts.join(', ');
      const formatted = loc.formatted_address || '';
      const floorMatch = formatted.match(/Floor:\s*([^\n]+)/);
      const doorbellMatch = formatted.match(/Name \/ Number on the door:\s*([^\n]+)/);
      floor = floorMatch ? floorMatch[1].trim() : null;
      doorbell = doorbellMatch ? doorbellMatch[1].trim() : null;
    } else if (isTakeaway) {
      address = i18n.t('orders:pickupAtStore');
    }
  } else if (order.platform_name === 'box') {
    const user = orderJson.user || {};
    name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || unknownCustomer;
    phone = orderJson.orderContactPhone || '';
    isTakeaway = orderJson.orderShippingType === 'takeAway' || orderJson.orderShippingType === 'takeaway';
    if (!isTakeaway && orderJson.address) {
      const addr = orderJson.address;
      const parts = [];
      if (addr.street) parts.push(addr.street);
      if (addr.streetNo) parts.push(addr.streetNo);
      if (addr.city) parts.push(addr.city);
      if (addr.postalCode) parts.push(addr.postalCode);
      address = parts.length > 0 ? parts.join(', ') : '';
      floor = addr.floor || null;
      doorbell = addr.nameAtBell || null;
    } else if (isTakeaway) {
      address = i18n.t('orders:pickupAtStore');
    }
  } else {
    // Efood
    const efoodOrder = orderJson.orders?.[0] || {};
    const customer = efoodOrder.customer || {};
    name = `${customer.name || ''} ${customer.surname || ''}`.trim() || unknownCustomer;
    phone = customer.telephone || customer.alternate_telephone || '';
    floor = customer.floor || null;
    doorbell = customer.doorbell || null;
    const parts = [];
    if (customer.street) parts.push(customer.street);
    if (customer.street_number) parts.push(customer.street_number);
    if (customer.area) parts.push(customer.area);
    if (customer.city) parts.push(customer.city);
    if (customer.postal_code) parts.push(customer.postal_code);
    address = parts.length > 0 ? parts.join(', ') : '';
  }

  return { name, phone, address, floor, doorbell, isTakeaway, isPreOrder, preOrderTime, notes, summary };
}
